// SPDX-License-Identifier: AGPL-3.0-or-later

import {GroupDMAvatar} from '@app/features/app/components/shared/GroupDMAvatar';
import {Attachment} from '@app/features/channel/components/embeds/attachments/Attachment';
import {AttachmentMosaic} from '@app/features/channel/components/embeds/attachments/AttachmentMosaic';
import {Embed} from '@app/features/channel/components/embeds/Embed';
import {GiftEmbed} from '@app/features/channel/components/GiftEmbed';
import {InviteEmbed} from '@app/features/channel/components/InviteEmbed';
import {getAttachmentRenderingState} from '@app/features/channel/components/MessageAttachmentStateUtils';
import styles from '@app/features/channel/components/MessageAttachments.module.css';
import {MessageReactions} from '@app/features/channel/components/MessageReactions';
import {useMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import {ThemeEmbed} from '@app/features/channel/components/ThemeEmbed';
import {TimestampWithTooltip} from '@app/features/channel/components/TimestampWithTooltip';
import {useStickerAnimation} from '@app/features/emoji/hooks/useStickerAnimation';
import Sticker from '@app/features/emoji/state/Sticker';
import {GuildIcon} from '@app/features/guild/components/popouts/GuildIcon';
import Guilds from '@app/features/guild/state/Guilds';
import {SafeMarkdown} from '@app/features/messaging/components/markdown';
import {MarkdownContext} from '@app/features/messaging/components/markdown/renderers/RendererTypes';
import {useNSFWMedia} from '@app/features/messaging/hooks/useNSFWMedia';
import type {Message} from '@app/features/messaging/models/Message';
import {useForwardedMessageContext} from '@app/features/messaging/utils/ForwardedMessageUtils';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import markupStyles from '@app/features/theme/styles/Markup.module.css';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import nsfwStyles from '@app/features/theme/styles/NSFWBlur.module.css';
import {StickerInlineMenuItems} from '@app/features/ui/action_menu/items/StickerContextMenuItems';
import {MessageContextMenu} from '@app/features/ui/action_menu/MessageContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import UserSettings from '@app/features/user/state/UserSettings';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {ChannelTypes} from '@app/vendor/constants/ChannelConstants';
import type {MessageEmbed} from '@app/vendor/schema/domains/message/EmbedSchemas';
import type {
	MessageAttachment,
	MessageSnapshot,
	MessageStickerItem,
} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {Trans} from '@lingui/react/macro';
import {ArrowBendUpRightIcon, CaretRightIcon, HashIcon, NotePencilIcon, SpeakerHighIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

const ForwardedFromSource = observer(({message}: {message: Message}) => {
	const {sourceChannel, sourceGuild, sourceUser, hasAccessToSource, displayName} = useForwardedMessageContext(message);
	const handleJumpToOriginal = useCallback(() => {
		if (message.messageReference && sourceChannel) {
			goToMessage(message.messageReference.channel_id, message.messageReference.message_id);
		}
	}, [message.messageReference, sourceChannel]);
	const renderChannelIcon = useCallback(() => {
		if (!sourceChannel) return null;
		const iconSize = 16;
		if (sourceChannel.type === ChannelTypes.DM_PERSONAL_NOTES) {
			return <NotePencilIcon className={styles.forwardedSourceIcon} weight="fill" size={iconSize} />;
		}
		if (sourceChannel.type === ChannelTypes.DM && sourceUser) {
			return (
				<div className={styles.forwardedSourceAvatar}>
					<Avatar user={sourceUser} size={iconSize} status={null} />
				</div>
			);
		}
		if (sourceChannel.type === ChannelTypes.GROUP_DM) {
			return (
				<div className={styles.forwardedSourceAvatar}>
					<GroupDMAvatar channel={sourceChannel} size={iconSize} />
				</div>
			);
		}
		if (sourceChannel.type === ChannelTypes.GUILD_VOICE) {
			return <SpeakerHighIcon className={styles.forwardedSourceIcon} weight="fill" size={iconSize} />;
		}
		return <HashIcon className={styles.forwardedSourceIcon} weight="bold" size={iconSize} />;
	}, [sourceChannel, sourceUser]);
	if (!hasAccessToSource || !sourceChannel || !displayName || !message.messageReference) {
		return null;
	}
	if (
		sourceChannel.type === ChannelTypes.DM ||
		sourceChannel.type === ChannelTypes.GROUP_DM ||
		sourceChannel.type === ChannelTypes.DM_PERSONAL_NOTES
	) {
		return (
			<FocusRing>
				<button type="button" onClick={handleJumpToOriginal} className={styles.forwardedSourceButton}>
					<span className={styles.forwardedSourceLabel}>
						<Trans>Forwarded from</Trans>
					</span>
					<span className={styles.forwardedSourceInfo}>
						{renderChannelIcon()}
						<span className={styles.forwardedSourceName}>{displayName}</span>
					</span>
				</button>
			</FocusRing>
		);
	}
	if (sourceGuild) {
		return (
			<FocusRing>
				<button type="button" onClick={handleJumpToOriginal} className={styles.forwardedSourceButton}>
					<span className={styles.forwardedSourceLabel}>
						<Trans>Forwarded from</Trans>
					</span>
					<span className={styles.forwardedSourceInfo}>
						<GuildIcon
							id={sourceGuild.id}
							name={sourceGuild.name}
							icon={sourceGuild.icon}
							className={styles.forwardedSourceGuildIcon}
							sizePx={16}
						/>
						<span className={styles.forwardedSourceName}>{sourceGuild.name}</span>
						<CaretRightIcon className={styles.forwardedSourceChevron} weight="bold" size={12} />
						{renderChannelIcon()}
						<span className={styles.forwardedSourceName}>{displayName}</span>
					</span>
				</button>
			</FocusRing>
		);
	}
	return null;
});
interface ForwardedMessageContentProps {
	message: Message;
	snapshot: MessageSnapshot;
	shouldAnimate: boolean;
	interactionHandlers: Record<string, unknown>;
	onDelete?: (bypassConfirm?: boolean) => void;
}
export const ForwardedMessageContent = observer(
	({message, snapshot, shouldAnimate, interactionHandlers, onDelete}: ForwardedMessageContentProps) => {
		const snapshotIndex = 0;
		const snapshotEditedTimestamp = snapshot.edited_timestamp ? new Date(snapshot.edited_timestamp) : null;
		return (
			<div className={styles.forwardedContainer}>
				<div className={styles.forwardedBar} />
				<div className={styles.forwardedContent}>
					<div className={styles.forwardedHeader}>
						<ArrowBendUpRightIcon className={styles.forwardedIcon} weight="bold" />
						<span className={styles.forwardedLabel}>
							<Trans>Forwarded</Trans>
						</span>
					</div>
					{snapshot.content && (
						<div className={clsx(markupStyles.markup)} data-search-highlight-scope="message">
							<SafeMarkdown
								content={snapshot.content}
								options={{
									context: MarkdownContext.STANDARD_WITH_JUMBO,
									messageId: message.id,
									channelId: message.channelId,
								}}
							/>
							{snapshotEditedTimestamp && (
								<TimestampWithTooltip date={snapshotEditedTimestamp} className={messageStyles.editedTimestamp}>
									<span className={messageStyles.editedLabel}>
										{' '}
										<Trans>(edited)</Trans>
									</span>
								</TimestampWithTooltip>
							)}
						</div>
					)}
					{snapshot.attachments && snapshot.attachments.length > 0 && (
						<div className={styles.attachmentsContainer}>
							{(() => {
								const {enrichedAttachments, mediaAttachments, shouldUseMosaic} = getAttachmentRenderingState(
									snapshot.attachments,
								);
								return (
									<>
										{shouldUseMosaic && (
											<AttachmentMosaic
												attachments={mediaAttachments}
												message={message}
												snapshotIndex={snapshotIndex}
												onDelete={onDelete}
											/>
										)}
										{enrichedAttachments.map((attachment: MessageAttachment) => (
											<Attachment
												key={attachment.id}
												attachment={attachment}
												snapshotIndex={snapshotIndex}
												message={message}
												renderInMosaic={shouldUseMosaic}
												onDelete={onDelete}
											/>
										))}
									</>
								);
							})()}
						</div>
					)}
					{snapshot.embeds && snapshot.embeds.length > 0 && UserSettings.getRenderEmbeds() && (
						<div className={styles.attachmentsContainer}>
							{snapshot.embeds.map((embed: MessageEmbed, index: number) => {
								const embedKey = `${embed.id}-${index}`;
								return (
									<Embed
										embed={embed}
										key={embedKey}
										message={message}
										embedIndex={index}
										contextualEmbeds={snapshot.embeds}
										onDelete={onDelete}
										isPreview={true}
									/>
								);
							})}
						</div>
					)}
					{snapshot.stickers && snapshot.stickers.length > 0 && (
						<div className={styles.stickersContainer}>
							{snapshot.stickers.map((sticker: MessageStickerItem) => (
								<StickerItem
									key={sticker.id}
									sticker={sticker}
									message={message}
									shouldAnimate={shouldAnimate}
									interactionHandlers={interactionHandlers}
									handleDelete={onDelete}
								/>
							))}
						</div>
					)}
					<ForwardedFromSource message={message} />
				</div>
			</div>
		);
	},
);
interface StickerItemProps {
	sticker: MessageStickerItem;
	message: Message;
	shouldAnimate: boolean;
	interactionHandlers: Record<string, unknown>;
	handleDelete?: (bypassConfirm?: boolean) => void;
}
const StickerItem = observer(
	({sticker, message, shouldAnimate, interactionHandlers, handleDelete}: StickerItemProps) => {
		const stickerUrl = AvatarUtils.getStickerURL({
			id: sticker.id,
			animated: shouldAnimate,
			size: 320,
		});
		const stickerRecord = Sticker.getStickerById(sticker.id);
		const guild = stickerRecord?.guildId ? Guilds.getGuild(stickerRecord.guildId) : null;
		const isNSFW = !!stickerRecord?.nsfw || !!sticker.nsfw;
		const {shouldBlur, shouldBlock, canReveal, reveal} = useNSFWMedia(isNSFW, message.channelId);
		const tooltipContent = () => (
			<div className={styles.stickerTooltip}>
				<span className={styles.stickerName}>{sticker.name}</span>
				{guild && (
					<div className={styles.stickerGuildInfo}>
						<GuildIcon
							id={guild.id}
							name={guild.name}
							icon={guild.icon}
							className={styles.stickerGuildIcon}
							sizePx={16}
						/>
						<span className={styles.stickerGuildName}>{guild.name}</span>
					</div>
				)}
			</div>
		);
		const handleContextMenu = (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			const stickerForMenu = stickerRecord ?? {
				id: sticker.id,
				guildId: '',
				name: sticker.name,
				description: '',
				tags: [],
				url: stickerUrl,
				animated: sticker.animated,
				nsfw: Boolean(sticker.nsfw),
				user: undefined,
			};
			ContextMenuCommands.openFromEvent(e, ({onClose}) => (
				<MessageContextMenu
					message={message}
					onClose={onClose}
					onDelete={handleDelete!}
					inlineStickerOrEmojiItems={<StickerInlineMenuItems sticker={stickerForMenu} onClose={onClose} />}
				/>
			));
		};
		const handleRevealClick = useCallback(
			(e: React.MouseEvent) => {
				if (shouldBlur && canReveal) {
					e.preventDefault();
					e.stopPropagation();
					reveal();
				}
			},
			[shouldBlur, canReveal, reveal],
		);
		if (shouldBlock) {
			return null;
		}
		return (
			<Tooltip key={sticker.id} text={tooltipContent}>
				<FocusRing>
					<div
						role="img"
						className={styles.stickerWrapper}
						data-message-sticker="true"
						onContextMenu={handleContextMenu}
						onClick={shouldBlur ? handleRevealClick : undefined}
						onKeyDown={
							shouldBlur
								? (e: React.KeyboardEvent) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleRevealClick(e as unknown as React.MouseEvent);
										}
									}
								: undefined
						}
						{...interactionHandlers}
					>
						<img
							src={stickerUrl}
							alt={stickerRecord?.description || sticker.name}
							className={clsx(styles.stickerImage, shouldBlur && nsfwStyles.nsfwStickerBlurred)}
							width="160"
							height="160"
						/>
					</div>
				</FocusRing>
			</Tooltip>
		);
	},
);
export const MessageAttachments = observer(() => {
	const {message, handleDelete, previewContext, onPopoutToggle, readonlyPreview} = useMessageViewContext();
	const isPreview = Boolean(previewContext);
	const reactionsIsPreview = isPreview || Boolean(readonlyPreview);
	const {shouldAnimate, interactionHandlers} = useStickerAnimation();
	return (
		<>
			{message.messageSnapshots && message.messageSnapshots.length > 0 && (
				<ForwardedMessageContent
					message={message}
					snapshot={message.messageSnapshots[0]}
					shouldAnimate={shouldAnimate}
					interactionHandlers={interactionHandlers}
					onDelete={handleDelete}
				/>
			)}
			{message.invites.map((code) => (
				<FocusRing key={code}>
					<InviteEmbed code={code} />
				</FocusRing>
			))}
			{message.themes.map((themeId) => (
				<FocusRing key={themeId}>
					<ThemeEmbed themeId={themeId} />
				</FocusRing>
			))}
			{message.gifts.map((code) => (
				<FocusRing key={code}>
					<GiftEmbed code={code} />
				</FocusRing>
			))}
			{message.stickers && message.stickers.length > 0 && (
				<div className={styles.stickersContainer}>
					{message.stickers.map((sticker: MessageStickerItem) => (
						<StickerItem
							key={sticker.id}
							sticker={sticker}
							message={message}
							shouldAnimate={shouldAnimate}
							interactionHandlers={interactionHandlers}
							handleDelete={handleDelete}
						/>
					))}
				</div>
			)}
			{(() => {
				const {enrichedAttachments, mediaAttachments, shouldUseMosaic} = getAttachmentRenderingState(
					message.attachments,
				);
				return (
					<>
						{shouldUseMosaic && (
							<AttachmentMosaic
								attachments={mediaAttachments}
								message={message}
								isPreview={isPreview}
								onDelete={handleDelete}
							/>
						)}
						{enrichedAttachments.map((attachment) => (
							<Attachment
								key={attachment.id}
								attachment={attachment}
								isPreview={isPreview}
								message={message}
								renderInMosaic={shouldUseMosaic}
								onDelete={handleDelete}
							/>
						))}
					</>
				);
			})()}
			{UserSettings.getRenderEmbeds() &&
				!message.suppressEmbeds &&
				message.embeds.map((embed, index) => {
					const embedKey = `${embed.id}-${index}`;
					return (
						<Embed
							embed={embed}
							key={embedKey}
							message={message}
							embedIndex={index}
							onDelete={handleDelete}
							isPreview={isPreview}
						/>
					);
				})}
			{UserSettings.getRenderReactions() && message.reactions.length > 0 && (
				<MessageReactions message={message} isPreview={reactionsIsPreview} onPopoutToggle={onPopoutToggle} />
			)}
		</>
	);
});
